import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PROJECTS } from '../../data/config';
import { Github, ExternalLink, Star, GitFork, Users } from 'lucide-react';

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
  },
};

const Projects = () => {
  const [filter, setFilter] = useState('All');
  const categories = ['All', ...new Set(PROJECTS.map(p => p.category))];

  const filteredProjects = filter === 'All'
    ? PROJECTS
    : PROJECTS.filter(p => p.category === filter);

  return (
    <section id="projects" className="section-container">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-16 max-w-3xl">
          <p className="text-[0.7rem] uppercase tracking-[0.4em] text-coffee-300 mb-5">Projects</p>
          <h2 className="text-5xl md:text-7xl font-bold mb-6 gradient-text leading-[1.05]">
            Things I've built.
          </h2>
          <p className="text-lg md:text-xl text-cream-200/80 leading-relaxed">
            Open-source tools and platforms that power modern infrastructure.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-start gap-2 mb-12">
          {categories.map((category) => {
            const active = filter === category;
            return (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 border ${
                  active
                    ? 'bg-cream-100/12 text-cream-100 border-caramel-400/40'
                    : 'bg-transparent text-coffee-300 border-cream-100/10 hover:text-cream-100 hover:border-cream-100/25'
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>

        {/* Projects Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <motion.div
                key={project.id}
                layout
                variants={cardVariants}
                className="glass-card overflow-hidden group flex flex-col"
              >
                {/* Project Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-espresso-900 via-espresso-900/50 to-transparent"></div>

                  {/* Category Badge — single, top-right, minimal */}
                  <div className="absolute top-4 right-4">
                    <span className="px-2.5 py-1 text-[0.65rem] uppercase tracking-wider rounded-full bg-espresso-900/70 backdrop-blur-md text-caramel-300 border border-cream-100/15">
                      {project.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-bold text-cream-100 mb-2 group-hover:text-cream-200 transition-colors">
                    {project.title}
                  </h3>

                  <p className="text-cream-200/80 text-sm mb-4 line-clamp-3">
                    {project.description}
                  </p>

                  {/* Metrics */}
                  <div className="flex items-center gap-4 mb-4 text-sm text-caramel-300">
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4" />
                      {project.metrics.stars}
                    </span>
                    <span className="flex items-center gap-1">
                      <GitFork className="w-4 h-4" />
                      {project.metrics.forks}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {project.metrics.users}
                    </span>
                  </div>

                  {/* Tech Stack */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.techStack.slice(0, 4).map((tech, i) => (
                      <span key={i} className="tech-badge text-xs px-3 py-1">
                        {tech}
                      </span>
                    ))}
                    {project.techStack.length > 4 && (
                      <span className="tech-badge text-xs px-3 py-1">
                        +{project.techStack.length - 4}
                      </span>
                    )}
                  </div>

                  {/* Links — pinned to bottom */}
                  <div className="flex gap-3 mt-auto">
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary flex-1 flex items-center justify-center gap-2 py-2.5 text-sm"
                    >
                      <Github className="w-4 h-4" />
                      Code
                    </a>
                    <a
                      href={project.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary flex-1 flex items-center justify-center gap-2 py-2.5 text-sm group/demo"
                    >
                      <ExternalLink className="w-4 h-4 transition-transform group-hover/demo:translate-x-0.5" />
                      Demo
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
